/* Patırtı common elements and storefront API. No external dependencies. */
(function () {
  'use strict';
  const config = window.PatirtiConfig || {};
  const P = window.Patirti = window.Patirti || {};
  const strings = config.strings || {};
  const text = (key, fallback) => strings[key] || fallback;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const routes = config.routes || {};
  const route = (key, fallback) => {
    const aliases = { cart: 'cart_url', cartAdd: 'cart_add_url', cartChange: 'cart_change_url', cartUpdate: 'cart_update_url', search: 'search_url', predictiveSearch: 'predictive_search_url' };
    const short = { cartAdd: 'add', cartChange: 'change', cartUpdate: 'update' };
    let value = routes[key] || routes[short[key]] || routes[aliases[key]] || root + fallback;
    if (key.indexOf('cart') === 0 && !value.endsWith('.js')) value += '.js';
    return value;
  };
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const behavior = () => reduced() ? 'auto' : 'smooth';
  const listen = (scope, target, name, fn, options) => {
    target.addEventListener(name, fn, options);
    scope.cleanups.push(() => target.removeEventListener(name, fn, options));
  };
  P.events = P.events || new EventTarget();
  P.emit = (name, detail) => {
    P.events.dispatchEvent(new CustomEvent(name, { detail }));
    document.dispatchEvent(new CustomEvent(name, { detail }));
  };
  P.money = cents => {
    const value = Number(cents);
    if (!Number.isFinite(value)) return '';
    const currency = config.currency || 'TRY';
    const format = new Intl.NumberFormat(config.locale || 'tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return currency === 'TRY' ? format.format(value / 100) + ' TL' :
      new Intl.NumberFormat(config.locale || 'tr-TR', { style: 'currency', currency }).format(value / 100);
  };
  async function request(url, payload) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(url, {
        method: payload ? 'POST' : 'GET', credentials: 'same-origin', signal: controller.signal,
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: payload ? JSON.stringify(payload) : undefined
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || text('cartError', text('error', 'Sepet güncellenemedi. Lütfen tekrar deneyin.')));
      return data;
    } finally { clearTimeout(timer); }
  }
  let cartQueue = Promise.resolve();
  const enqueue = action => {
    const next = cartQueue.then(action);
    cartQueue = next.catch(() => {});
    return next;
  };
  const getCart = async () => {
    const cart = await request(route('cart', 'cart.js'));
    P.emit('cart:updated', { cart });
    return cart;
  };
  P.cart = {
    get: getCart,
    add(items) {
      const list = Array.isArray(items) ? items : [items];
      if (!list.length || list.some(item => !item || !/^\d+$/.test(String(item.id)) ||
        !Number.isInteger(Number(item.quantity === undefined ? 1 : item.quantity)) || Number(item.quantity === undefined ? 1 : item.quantity) < 1)) {
        return Promise.reject(new Error(text('invalidQuantity', 'Geçerli bir ürün ve adet seçin.')));
      }
      return enqueue(async () => {
        const data = await request(route('cartAdd', 'cart/add.js'), { items: list.map(item => {
          const clean = { id: item.id, quantity: Number(item.quantity === undefined ? 1 : item.quantity) };
          ['properties', 'selling_plan'].forEach(key => { if (item[key] !== undefined) clean[key] = item[key]; });
          return clean;
        }) });
        const added = data.items || [data];
        added.forEach(item => { if (P.sp && item.product_id) P.sp.logAtc(item.product_id); });
        let cart = null;
        try { cart = await getCart(); } catch (error) { P.emit('cart:error', { error }); }
        P.emit('cart:added', { items: added, cart });
        return data;
      });
    },
    change(key, qty) {
      if (!key || !Number.isInteger(Number(qty)) || Number(qty) < 0) return Promise.reject(new Error(text('invalidQuantity', 'Geçerli bir adet seçin.')));
      return enqueue(async () => {
        const cart = await request(route('cartChange', 'cart/change.js'), { id: String(key), quantity: Number(qty) });
        P.emit('cart:updated', { cart });
        return cart;
      });
    },
    applyDiscount(code) {
      if (typeof code !== 'string' || code.length > 255) return Promise.reject(new Error(text('invalidDiscount', 'Geçerli bir indirim kodu girin.')));
      return enqueue(async () => {
        const cart = await request(route('cartUpdate', 'cart/update.js'), { discount: code.trim() });
        P.emit('cart:updated', { cart });
        return cart;
      });
    }
  };
  class Component extends HTMLElement {
    connectedCallback() { this.cleanups = []; }
    disconnectedCallback() { (this.cleanups || []).forEach(fn => fn()); this.cleanups = []; }
  }
  class SliderRow extends Component {
    connectedCallback() {
      super.connectedCallback();
      this.track = this.querySelector('[data-slider-track], .slider-track, .slider-row__track');
      if (!this.track) return;
      this.prev = this.querySelector('[data-slider-prev]');
      this.next = this.querySelector('[data-slider-next]');
      const move = direction => this.track.scrollBy({ left: direction * this.track.clientWidth * .85, behavior: behavior() });
      if (this.prev) listen(this, this.prev, 'click', () => move(-1));
      if (this.next) listen(this, this.next, 'click', () => move(1));
      if (!this.track.hasAttribute('tabindex')) this.track.tabIndex = 0;
      listen(this, this.track, 'keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault(); move(event.key === 'ArrowLeft' ? -1 : 1);
      });
      const update = () => {
        const remaining = this.track.scrollWidth - this.track.clientWidth - this.track.scrollLeft;
        if (this.prev) this.prev.disabled = this.track.scrollLeft <= 1;
        if (this.next) this.next.disabled = remaining <= 1;
      };
      listen(this, this.track, 'scroll', update, { passive: true });
      if ('ResizeObserver' in window) { const observer = new ResizeObserver(update); observer.observe(this.track); this.cleanups.push(() => observer.disconnect()); }
      listen(this, window, 'resize', update, { passive: true });
      update();
    }
  }
  class ProductGalleryMini extends Component {
    connectedCallback() {
      super.connectedCallback();
      this.track = this.querySelector('[data-gallery-track], .pc-gallery__track');
      this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]')).slice(0, 5);
      this.dots = Array.from(this.querySelectorAll('[data-gallery-dot], [data-gallery-index]'));
      if (!this.track || !this.slides.length) return;
      this.dots.forEach((dot, index) => listen(this, dot, 'click', event => { event.preventDefault(); event.stopPropagation(); this.go(index); }));
      listen(this, this, 'pointermove', event => {
        if (event.pointerType !== 'mouse' || this.hasAttribute('data-video-active') || !window.matchMedia('(min-width: 1024px)').matches || event.target.closest('button')) return;
        const box = this.getBoundingClientRect();
        this.go(Math.min(this.slides.length - 1, Math.max(0, Math.floor((event.clientX - box.left) / box.width * this.slides.length))), false);
      });
      listen(this, this, 'pointerleave', () => { if (window.matchMedia('(hover: hover)').matches) this.go(0, false); });
      listen(this, this.track, 'scroll', () => this.activate(Math.round(this.track.scrollLeft / Math.max(this.track.clientWidth, 1))), { passive: true });
      this.activate(0);
    }
    activate(index) { this.dots.forEach((dot, i) => { dot.classList.toggle('is-active', i === index); dot.setAttribute('aria-current', i === index ? 'true' : 'false'); }); }
    go(index, smooth = true) { this.track.scrollTo({ left: index * this.track.clientWidth, behavior: smooth ? behavior() : 'auto' }); this.activate(index); }
  }
  const opened = [];
  let priorOverflow = '';
  const focusable = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  class ModalDialog extends Component {
    connectedCallback() {
      super.connectedCallback();
      this.setAttribute('role', 'dialog'); this.setAttribute('aria-modal', 'true');
      this.tabIndex = -1;
      if (!this.hasAttribute('open')) this.hidden = true;
      listen(this, this, 'click', event => {
        if (event.target.closest('[data-dialog-close]') || event.target.hasAttribute('data-dialog-backdrop')) this.close();
      });
      listen(this, document, 'keydown', event => {
        if (opened[opened.length - 1] !== this) return;
        if (event.key === 'Escape') { event.preventDefault(); this.close(); }
        if (event.key !== 'Tab') return;
        const nodes = Array.from(this.querySelectorAll(focusable)).filter(node => node.getClientRects().length && !node.closest('[hidden]'));
        const first = nodes[0] || this, last = nodes[nodes.length - 1] || this;
        if (!nodes.length || (event.shiftKey && (document.activeElement === first || document.activeElement === this))) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && (document.activeElement === last || !this.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
      });
      if (this.hasAttribute('open')) this.open();
    }
    open(trigger) {
      if (opened.includes(this)) return;
      this.returnFocus = trigger || document.activeElement;
      if (!opened.length) { priorOverflow = document.body.style.overflow; document.body.style.overflow = 'hidden'; }
      opened.push(this); this.hidden = false; this.setAttribute('open', '');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      const target = Array.from(this.querySelectorAll('[autofocus],' + focusable)).find(node => node.getClientRects().length) || this;
      target.focus(); this.dispatchEvent(new CustomEvent('dialog:opened', { bubbles: true }));
    }
    close(restore = true) {
      const index = opened.indexOf(this);
      if (index < 0) return;
      opened.splice(index, 1); this.hidden = true; this.removeAttribute('open');
      if (!opened.length) document.body.style.overflow = priorOverflow;
      if (this.returnFocus && this.returnFocus.isConnected) {
        this.returnFocus.setAttribute('aria-expanded', 'false');
        if (restore) this.returnFocus.focus();
      }
      this.dispatchEvent(new CustomEvent('dialog:closed', { bubbles: true }));
    }
    disconnectedCallback() { this.close(false); super.disconnectedCallback(); }
  }
  class DrawerPanel extends ModalDialog {}
  class TabsAnchor extends Component {
    connectedCallback() {
      super.connectedCallback();
      const links = Array.from(this.querySelectorAll('a[href^="#"]'));
      const pairs = links.map(link => ({ link, target: document.getElementById(decodeURIComponent(link.hash.slice(1))) })).filter(pair => pair.target);
      const activate = link => links.forEach(item => { item.classList.toggle('is-active', item === link); if (item === link) item.setAttribute('aria-current', 'location'); else item.removeAttribute('aria-current'); });
      pairs.forEach(({ link, target }) => listen(this, link, 'click', event => {
        event.preventDefault(); target.scrollIntoView({ behavior: behavior(), block: 'start' }); activate(link);
        history.replaceState(null, '', link.hash);
      }));
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(entries => {
          entries.forEach(entry => { if (entry.isIntersecting) { const match = pairs.find(pair => pair.target === entry.target); if (match) activate(match.link); } });
        }, { rootMargin: '-15% 0px -65% 0px', threshold: 0 });
        pairs.forEach(pair => observer.observe(pair.target)); this.cleanups.push(() => observer.disconnect());
      }
    }
  }
  function shippingDays(value) {
    const days = new Set();
    String(value || '').split(',').forEach(part => {
      if (/^\s*[0-7]\s*-\s*[0-7]\s*$/.test(part)) {
        const range = part.split('-').map(Number);
        for (let day = range[0]; day <= range[1]; day++) days.add(day % 7);
      } else if (/^\s*[0-7]\s*$/.test(part)) days.add(Number(part) % 7);
    });
    return days;
  }
  class CountdownCutoff extends Component {
    connectedCallback() {
      super.connectedCallback();
      const enabled = this.dataset.enabled === 'true' || (this.dataset.enabled !== 'false' && config.cutoffEnabled === true);
      const hourValue = this.dataset.cutoffHour === undefined ? config.cutoffHour : this.dataset.cutoffHour;
      const minuteValue = this.dataset.cutoffMinute === undefined ? config.cutoffMinute : this.dataset.cutoffMinute;
      const hour = Number(hourValue), minute = Number(minuteValue);
      const days = shippingDays(this.dataset.days || config.shippingDays);
      if (!enabled || hourValue === '' || hourValue === undefined || !Number.isInteger(hour) || hour < 0 || hour > 23 || !Number.isInteger(minute) || minute < 0 || minute > 59 || !days.size) { this.hidden = true; return; }
      const tick = () => {
        const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Istanbul', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
        const part = name => parts.find(item => item.type === name).value;
        const today = new Date(part('year') + '-' + part('month') + '-' + part('day') + 'T00:00:00+03:00');
        let target, offset;
        for (offset = 0; offset <= 7; offset++) {
          target = new Date(today.getTime() + offset * 86400000 + (hour * 60 + minute) * 60000);
          const weekday = new Date(target.getTime() + 3 * 3600000).getUTCDay();
          if (days.has(weekday) && target.getTime() > Date.now()) break;
        }
        const remaining = Math.max(0, Math.floor((target.getTime() - Date.now()) / 60000));
        const label = Math.floor(remaining / 60) + ' ' + text('hours', 'saat') + ' ' + remaining % 60 + ' ' + text('minutes', 'dakika');
        const day = offset === 0 ? text('today', 'Bugün') : offset === 1 ? text('tomorrow', 'Yarın') : new Intl.DateTimeFormat(config.locale || 'tr-TR', { timeZone: 'Europe/Istanbul', weekday: 'long' }).format(target);
        const timeNode = this.querySelector('[data-countdown-time]'), dayNode = this.querySelector('[data-countdown-day]');
        if (timeNode) timeNode.textContent = label;
        if (dayNode) dayNode.textContent = day;
        if (!timeNode && !dayNode) this.textContent = label + ' · ' + day;
        this.dataset.shippingDate = target.toISOString(); this.hidden = false;
      };
      tick(); const timer = setInterval(tick, 30000); this.cleanups.push(() => clearInterval(timer));
    }
  }
  class CouponCountdown extends Component {
    connectedCallback() {
      super.connectedCallback();
      const iso = this.dataset.endIso || '';
      const end = /T.*(?:Z|[+-]\d{2}:\d{2})$/.test(iso) ? Date.parse(iso) : NaN;
      const tick = () => {
        const left = end - Date.now();
        if (!Number.isFinite(end) || left <= 0) {
          this.hidden = true; const coupon = this.closest('[data-coupon-container]'); if (coupon) coupon.hidden = true;
          if (!this.expired) { this.expired = true; this.dispatchEvent(new CustomEvent('coupon:expired', { bubbles: true })); }
          return;
        }
        this.hidden = false;
        const minutes = Math.floor(left / 60000), days = Math.floor(minutes / 1440), hours = Math.floor(minutes % 1440 / 60);
        this.textContent = (days ? days + ' ' + text('days', 'gün') + ' ' : '') + hours + ' ' + text('hours', 'saat') + ' ' + minutes % 60 + ' ' + text('minutes', 'dakika');
      };
      tick(); if (this.expired) return; const timer = setInterval(tick, 1000); this.cleanups.push(() => clearInterval(timer));
    }
  }
  Object.entries({ 'slider-row': SliderRow, 'product-gallery-mini': ProductGalleryMini, 'modal-dialog': ModalDialog, 'drawer-panel': DrawerPanel, 'tabs-anchor': TabsAnchor, 'countdown-cutoff': CountdownCutoff, 'coupon-countdown': CouponCountdown }).forEach(([name, constructor]) => { if (!customElements.get(name)) customElements.define(name, constructor); });
  function recentSearches(value) {
    try {
      const data = JSON.parse(localStorage.getItem('patirti_recent_searches') || '[]');
      const list = Array.isArray(data) ? data.filter(item => typeof item === 'string').slice(0, 6) : [];
      if (!value) return list;
      localStorage.setItem('patirti_recent_searches', JSON.stringify([value].concat(list.filter(item => item !== value)).slice(0, 6)));
    } catch (error) { /* Storage can be unavailable in private browsing. */ }
    return [];
  }
  const cleanups = new WeakMap();
  function setupSearch(scope) {
    if (scope.dataset.searchReady) return;
    scope.dataset.searchReady = 'true';
    const input = scope.querySelector('[data-search-input]'), panel = scope.querySelector('[data-search-results]');
    if (!input || !panel) return;
    let timer, controller, sequence = 0;
    const close = () => { sequence++; if (controller) controller.abort(); clearTimeout(timer); panel.hidden = true; input.setAttribute('aria-expanded', 'false'); input.removeAttribute('aria-busy'); };
    cleanups.set(scope, () => { close(); delete scope.dataset.searchReady; });
    const show = () => { panel.hidden = !panel.childElementCount; input.setAttribute('aria-expanded', String(!panel.hidden)); };
    const heading = (key, fallback) => { const title = document.createElement('p'); title.className = 'predictive-search__heading'; title.textContent = text(key, fallback); panel.appendChild(title); };
    const append = (title, url, imageUrl) => {
      const safe = new URL(url, location.origin); if (safe.origin !== location.origin) return;
      const anchor = document.createElement('a'); anchor.href = safe.href; anchor.className = 'predictive-search__item';
      if (imageUrl) { const img = document.createElement('img'); img.src = imageUrl; img.alt = ''; img.width = 40; img.height = 60; img.loading = 'lazy'; anchor.appendChild(img); }
      const span = document.createElement('span'); span.textContent = title; anchor.appendChild(span); panel.appendChild(anchor);
    };
    const run = async () => {
      const query = input.value.trim().slice(0, 200), current = ++sequence;
      if (controller) controller.abort(); panel.replaceChildren();
      if (query.length < 2) { const recent = recentSearches(); if (recent.length) heading('recentSearches', 'Son aramalar'); recent.forEach(value => append(value, route('search', 'search') + '?q=' + encodeURIComponent(value))); show(); return; }
      controller = new AbortController(); input.setAttribute('aria-busy', 'true');
      const activeController = controller;
      const timeout = setTimeout(() => activeController.abort(), 10000);
      try {
        const url = new URL(route('predictiveSearch', 'search/suggest.json'), location.origin);
        if (!url.pathname.endsWith('.json')) url.pathname += '.json';
        url.searchParams.set('q', query); url.searchParams.set('resources[type]', 'query,product,collection'); url.searchParams.set('resources[limit]', '5');
        const response = await fetch(url.href, { signal: controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('search');
        const data = await response.json(); if (current !== sequence) return;
        const results = data.resources && data.resources.results || {};
        if ((results.queries || []).length) heading('suggestions', 'Öneriler');
        (results.queries || []).forEach(item => append(item.text, item.url));
        if ((results.products || []).length) heading('products', 'Ürünler');
        (results.products || []).forEach(item => append(item.title, item.url, item.featured_image && item.featured_image.url));
        if ((results.collections || []).length) heading('collections', 'Koleksiyonlar');
        (results.collections || []).forEach(item => append(item.title, item.url));
        append(text('allResults', 'Tüm sonuçları gör'), route('search', 'search') + '?q=' + encodeURIComponent(query)); show();
      } catch (error) {
        if (error.name !== 'AbortError' && current === sequence) { append(text('allResults', 'Tüm sonuçları gör'), route('search', 'search') + '?q=' + encodeURIComponent(query)); show(); }
      } finally { clearTimeout(timeout); if (current === sequence) input.removeAttribute('aria-busy'); }
    };
    input.addEventListener('input', () => { close(); timer = setTimeout(run, 220); });
    input.addEventListener('focus', run);
    scope.addEventListener('focusout', event => { if (!scope.contains(event.relatedTarget)) close(); });
    scope.addEventListener('keydown', event => {
      if (event.key === 'Escape') { input.focus(); close(); return; }
      if (!['ArrowDown', 'ArrowUp'].includes(event.key) || panel.hidden) return;
      const links = Array.from(panel.querySelectorAll('a')), index = links.indexOf(document.activeElement);
      event.preventDefault(); const target = links[event.key === 'ArrowDown' ? (index + 1) % links.length : (index - 1 + links.length) % links.length]; if (target) target.focus();
    });
    const form = scope.matches('form') ? scope : scope.querySelector('form');
    if (form) form.addEventListener('submit', () => { const query = input.value.trim().slice(0, 200); if (query) recentSearches(query); });
    panel.addEventListener('click', () => { if (input.value.trim()) recentSearches(input.value.trim().slice(0, 200)); });
  }
  const initialize = scope => {
    scope.querySelectorAll('[data-predictive-search]').forEach(setupSearch);
    const header = scope.querySelector('[data-sticky-header]');
    if (header && !header.dataset.stickyReady) {
      header.dataset.stickyReady = 'true';
      const spacer = document.createElement('div'); spacer.setAttribute('aria-hidden', 'true'); header.before(spacer);
      const sync = () => {
        const stuck = spacer.getBoundingClientRect().top <= 0 && window.scrollY > 0;
        const height = header.offsetHeight;
        header.classList.toggle('is-stuck', stuck); spacer.style.height = stuck ? height + 'px' : '0';
        ['position', 'top', 'left', 'right', 'z-index'].forEach((key, i) => {
          if (stuck) header.style.setProperty(key, ['fixed', '0', '0', '0', '40'][i]); else header.style.removeProperty(key);
        });
        document.documentElement.style.setProperty('--header-height', height + 'px');
      };
      window.addEventListener('scroll', sync, { passive: true }); window.addEventListener('resize', sync, { passive: true }); sync();
      cleanups.set(header, () => { window.removeEventListener('scroll', sync); window.removeEventListener('resize', sync); spacer.remove(); delete header.dataset.stickyReady; });
    }
    scope.querySelectorAll('.mega-menu').forEach(menu => {
      if (menu.dataset.menuReady) return; menu.dataset.menuReady = 'true';
      menu.addEventListener('mouseenter', () => { if (window.matchMedia('(hover: hover) and (min-width: 1024px)').matches) menu.open = true; });
      menu.addEventListener('mouseleave', () => { if (!menu.contains(document.activeElement)) menu.open = false; });
      menu.addEventListener('focusout', event => { if (!menu.contains(event.relatedTarget)) menu.open = false; });
      menu.addEventListener('keydown', event => { if (event.key === 'Escape') { menu.open = false; menu.querySelector('summary').focus(); } });
    });
    scope.querySelectorAll('details.footer-column').forEach(column => {
      if (column.dataset.footerReady) return; column.dataset.footerReady = 'true';
      const media = window.matchMedia('(min-width: 768px)'), sync = () => { column.open = media.matches; };
      media.addEventListener('change', sync); sync(); cleanups.set(column, () => media.removeEventListener('change', sync));
    });
  };
  /* On-demand card modules (colour strip, quick view): [data-module][data-module-src][data-module-css].
     Preloads on card hover/focus/touch; a click that arrives before the module is ready is replayed via P[module].handle(el). */
  const assetLoads = {};
  P.loadAsset = src => {
    const url = new URL(src, location.href);
    if (!/\.(js|css)$/.test(url.pathname)) return Promise.reject(new Error('asset'));
    return assetLoads[url.href] || (assetLoads[url.href] = new Promise((resolve, reject) => {
      const css = url.pathname.endsWith('.css'), node = document.createElement(css ? 'link' : 'script');
      if (css) { node.rel = 'stylesheet'; node.href = url.href; } else { node.src = url.href; node.async = true; }
      node.onload = () => resolve(); node.onerror = () => { delete assetLoads[url.href]; node.remove(); reject(new Error('asset')); };
      document.head.appendChild(node);
    }));
  };
  const loadModule = el => Promise.all([el.dataset.moduleCss, el.dataset.moduleSrc].filter(Boolean).map(P.loadAsset)).then(() => P[el.dataset.module]);
  const preloadModules = event => {
    const scope = event.target.closest && event.target.closest('[data-product-card], [data-module-src]');
    if (!scope) return;
    (scope.matches('[data-module-src]') ? [scope] : Array.from(scope.querySelectorAll('[data-module-src]')))
      .forEach(el => { if (!P[el.dataset.module]) loadModule(el).catch(() => {}); });
  };
  ['pointerover', 'focusin', 'touchstart'].forEach(type => document.addEventListener(type, preloadModules, { passive: true, capture: true }));
  document.addEventListener('click', event => {
    const el = event.target.closest('[data-module][data-module-src]');
    if (!el || P[el.dataset.module]) return;
    event.preventDefault(); event.stopPropagation();
    loadModule(el).then(module => { if (module && module.handle) module.handle(el); }).catch(() => {});
  }, true);
  document.addEventListener('click', async event => {
    const trigger = event.target.closest('[data-modal-open], [data-drawer-open]');
    if (trigger) { const modal = document.getElementById(trigger.dataset.modalOpen || trigger.dataset.drawerOpen); if (modal && modal.open) { event.preventDefault(); modal.open(trigger); } }
    const copy = event.target.closest('[data-copy-code]');
    if (copy) {
      try { await navigator.clipboard.writeText(copy.dataset.copyCode); copy.dataset.copied = 'true'; const status = copy.querySelector('[data-copy-status]'); if (status) status.textContent = text('copied', 'Kopyalandı'); }
      catch (error) { P.emit('copy:error', { error, code: copy.dataset.copyCode }); }
    }
    const back = event.target.closest('[data-back]');
    if (back && history.length > 1) { event.preventDefault(); history.back(); }
    const card = event.target.closest('[data-product-card][data-product-url]');
    if (card && !event.defaultPrevented && !event.target.closest('a, button, select, input, textarea, form, label')) {
      const url = new URL(card.dataset.productUrl, location.origin); if (url.origin === location.origin) location.assign(url.href);
    }
  });
  document.addEventListener('submit', async event => {
    const form = event.target.closest('[data-product-form], [data-product-card-form]'); if (!form) return;
    event.preventDefault(); if (form.dataset.submitting) return;
    const data = new FormData(form), button = form.querySelector('[type="submit"]'), errorNode = form.querySelector('[data-form-error], [data-card-error]');
    if (!form.reportValidity()) return;
    form.dataset.submitting = 'true'; if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); } if (errorNode) { errorNode.hidden = true; errorNode.textContent = ''; }
    try {
      const item = { id: data.get('id'), quantity: Number(data.get('quantity') || 1), properties: {} };
      data.forEach((value, key) => { const match = /^properties\[(.+)\]$/.exec(key); if (match && typeof value === 'string') item.properties[match[1]] = value; });
      if (data.get('selling_plan')) item.selling_plan = data.get('selling_plan');
      await P.cart.add([item]); form.dispatchEvent(new CustomEvent('product:added', { bubbles: true }));
    } catch (error) {
      if (errorNode) { errorNode.textContent = error.message; errorNode.hidden = false; } P.emit('cart:error', { error });
    } finally { delete form.dataset.submitting; if (button) { button.disabled = false; button.removeAttribute('aria-busy'); } }
  });
  P.events.addEventListener('cart:updated', event => {
    const cart = event.detail.cart || event.detail;
    document.querySelectorAll('[data-cart-count]').forEach(node => { node.textContent = String(cart.item_count || 0); node.hidden = !cart.item_count; });
  });
  /* Ürün kodu kopyala (snippets/product-code.liquid). Pano izni yoksa metni seçili bırakır. */
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-copy-code]');
    if (!button) return;
    const code = button.dataset.copyCode, done = button.querySelector('.product-code__done');
    const finish = () => { button.classList.add('is-copied'); if (done) done.textContent = button.dataset.copied || ''; clearTimeout(button.copyTimer); button.copyTimer = setTimeout(() => { button.classList.remove('is-copied'); if (done) done.textContent = ''; }, 1800); };
    const fallback = () => { const value = button.parentElement.querySelector('.product-code__value'); if (!value) return; const range = document.createRange(); range.selectNodeContents(value); const sel = window.getSelection(); sel.removeAllRanges(); sel.addRange(range); try { if (document.execCommand('copy')) finish(); } catch (_) { /* Seçili kalır. */ } };
    if (navigator.clipboard && window.isSecureContext) navigator.clipboard.writeText(code).then(finish, fallback); else fallback();
  });
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-predictive-search], [data-sticky-header], details.footer-column').forEach(node => { if (cleanups.has(node)) cleanups.get(node)(); });
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  else initialize(document);
})();
